import { compactParams } from '@repo/ui/lib/compact-params';
import type { FetchParams } from '@/api/models';

/**
 * Parametri di una list API ricavati dalla `search` tipizzata della rotta.
 *
 * Le API del backoffice rifiutano con 400 i parametri che non conoscono
 * (`forbidNonWhitelisted`): ogni lista dichiara i SOLI campi che il suo
 * endpoint accetta, ognuno con il lettore che porta il valore dell'URL nel
 * tipo dell'API. I parametri di sola interfaccia (`subTab`, `tab`, `show`,
 * i parametri dei pannelli) restano fuori perché nessuno li dichiara; il
 * filtro del tab si aggiunge a parte. Gli enum li valida già lo schema
 * della rotta; i vuoti (`''`, `null`, `[]`) si scartano qui.
 */

/** Dal valore della search al valore per l'API; `undefined` = non si invia. */
type ApiParamReader<TValue, TParam> = (value: TValue) => TParam | undefined;

/**
 * I lettori di una lista: solo chiavi che l'API conosce E che lo schema della
 * rotta dichiara, ognuna con il tipo del suo campo. Una chiave estranea, o un
 * lettore che restituisce il tipo sbagliato, non compila.
 */
export type ApiParamReaders<TSearch, TApi> = {
  [K in keyof TApi]?: K extends keyof TSearch
    ? ApiParamReader<TSearch[K], TApi[K]>
    : never;
};

/** Il valore così com'è, già validato dallo schema (testo, enum, date). */
export const valueParam = <TValue>(
  value: TValue | null | undefined
): TValue | undefined => value ?? undefined;

/** Solo testo: `sort` nello schema comune è libero (`z.any`). */
export const textParam = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

/**
 * Filtro sì/no, dal booleano dello schema o dall'id stringa di una combobox
 * (`'true'`/`'false'`); `false` resta un filtro, altro = nessun filtro.
 */
export const booleanParam = (
  value: boolean | string | null | undefined
): boolean | undefined => {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return undefined;
};

/** Ricerca e ordinamento, comuni a quasi tutte le liste. */
export const SEARCH_AND_SORT_PARAMS = {
  search: valueParam,
  sort: textParam,
} as const;

/**
 * I parametri per l'API: per ogni campo dichiarato il valore letto dalla
 * search, senza le chiavi vuote.
 */
export function pickApiParams<TSearch extends object, TApi extends object>(
  search: TSearch,
  readers: ApiParamReaders<TSearch, TApi>
): Partial<TApi> {
  const values = search as Record<string, unknown>;
  const entries = Object.entries(readers) as [
    string,
    ((value: unknown) => unknown) | undefined,
  ][];
  return compactParams(
    Object.fromEntries(
      entries.flatMap(([key, read]) => (read ? [[key, read(values[key])]] : []))
    )
  ) as Partial<TApi>;
}

/** Parametri di una lista che accetta solo la ricerca (né ordine né filtri). */
export type SearchOnlyApiParams = Pick<FetchParams, 'search'>;

/**
 * Per gli endpoint con la sola ricerca (accessi, commercialisti): l'ordine è
 * fisso sul server. Senza ricerca restituisce `{}`, la stessa chiave della
 * lista non filtrata.
 */
export function buildSearchOnlyApiParams(search: {
  search?: string | null;
}): SearchOnlyApiParams {
  return pickApiParams(search, { search: valueParam });
}
